import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Upload } from "lucide-react";

import { cn } from "@/lib/utils";
import type { DeadlineCard } from "./overview-model";

interface Props {
  cards: DeadlineCard[];
  className?: string;
  /** The Idag panel draws its own section heading. */
  hideHeading?: boolean;
}

/**
 * The DEADLINES cards from the sketch: amber for what is due, green once
 * done. Renders nothing when there is no deadline to show.
 */
export function DeadlinesColumn({
  cards,
  className,
  hideHeading = false,
}: Props) {
  const { t } = useTranslation("dashboard");
  if (cards.length === 0) return null;

  return (
    <div
      className={cn("flex min-w-[200px] flex-col gap-2.5", className)}
      data-testid="deadlines"
    >
      {hideHeading ? null : (
        <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.1em] text-g-warning">
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          {t("overview.deadlines.title")}
        </div>
      )}
      {cards.map((card) => {
        const warning = card.tone === "warning";
        const Icon = warning ? Upload : CheckCircle2;
        return (
          <div
            key={card.id}
            data-testid={`deadline-${card.id}`}
            data-tone={card.tone}
            className={cn(
              "flex flex-1 flex-col gap-1.5 rounded-g-lg border-[1.5px] p-[12px_14px]",
              warning
                ? "border-g-warning-fill bg-g-warning-tint"
                : "border-g-success-fill bg-g-success-tint",
            )}
          >
            <div className="flex items-center gap-2">
              <Icon
                className={cn(
                  "size-4 shrink-0",
                  warning ? "text-g-warning" : "text-g-success",
                )}
                aria-hidden="true"
              />
              <span className="min-w-0 text-[13.5px] font-bold text-g-ink-900">
                {card.title}
              </span>
            </div>
            <span
              className={cn(
                "text-[12px] font-bold",
                warning ? "text-g-warning" : "text-g-success",
              )}
            >
              {card.meta}
            </span>
            <Link
              to={card.cta.to}
              className={cn(
                "mt-0.5 inline-flex h-8 w-fit items-center gap-1.5 rounded-g-xs px-3 text-[12.5px] font-bold",
                warning
                  ? "bg-g-brand text-g-on-primary shadow-g-button"
                  : "bg-g-bg-card text-g-success",
              )}
            >
              {card.cta.label}
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        );
      })}
    </div>
  );
}
